<script setup>
// --------------------------------- Imports -------------------------------- //
import { onMounted, onUnmounted, ref } from 'vue';
import { CustomEditors } from '~/bi/components/widgets/table-widget/composables/custom-editors';
import { useCustomRenders } from '~/bi/components/widgets/table-widget/composables/custom-renders.js';
import { load_js_css_file, removeLoadedFile } from '~/common/utils/load-script.util';

// ---------------------------------- Props --------------------------------- //
const props = defineProps({
  biTableId: {
    type: String,
    default: 'bi-handsontable',
  },
  height: {
    type: String,
    default: '600px',
  },
  width: {
    type: String,
    default: '100%',
  },
  data: {
    type: Array,
    default: () => [],
  },
  columns: {
    type: Array,
    default: () => [],
  },
  columnConfig: {
    type: Object,
    default: () => ({}),
  },
  nestedHeaders: {
    type: Array,
    default: () => [],
  },
  showSkeletonLoader: {
    type: Boolean,
    default: false,
  },
  sortConfig: {
    type: Array,
    default: () => ([]),
  },
  rowHeaders: {
    type: Boolean,
    default: false,
  },
  colHeaders: {
    type: Function,
    default: null,
  },
  nestedRows: {
    type: Boolean,
    default: false,
  },
  getCellFormatting: {
    type: Function,
    default: () => null,
  },
  additionalTableSettings: {
    type: Object,
    default: () => ({}),
  },
  enableColumnSorting: {
    type: Boolean,
    default: true,
  },
  getCustomCellContent: {
    type: Function,
    default: null,
  },
  canEdit: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['columnResize', 'filterClicked', 'columnSort', 'tableInstance', 'afterLoadData', 'columnMove', 'selectionEnd']);

const { registerMultiSelect, registerDateTime } = CustomEditors();
const { tagsRenderer } = useCustomRenders();

const hotInstance = ref(null);
const isLoading = ref(false);

// Global registry of all BI Handsontable instances for PDF export access
if (!window.__biHotInstances) {
  window.__biHotInstances = new Map();
}

const table_settings = computed(() => {
  return {
    data: props.data,
    columns: props.columns,
    multiColumnSorting: props.enableColumnSorting && {
      initialConfig: (props.sortConfig || []).map(config => ({
        column: config.column || props.columns.findIndex(column => column.data === config.key),
        sortOrder: config.sortOrder,
      })),
    },
    // Nested headers support
    ...(props.nestedHeaders?.length && { nestedHeaders: props.nestedHeaders, collapsibleColumns: true }),

    // Nested rows support
    nestedRows: props.nestedRows,

    // Headers
    colHeaders: props.colHeaders ?? props.columns.map(col => col?.label || col?.data || col),
    rowHeaders: props.rowHeaders,

    // Optimized virtualization settings for smooth scrolling
    viewportRowRenderingOffset: 'auto',
    viewportColumnRenderingOffset: 'auto',
    renderAllRows: false,
    renderAllColumns: false,

    // Add filtering
    filters: true,
    dropdownMenu: ['filter_by_condition', 'filter_by_value', 'filter_action_bar'],

    // Pagination
    pagination: {
      pageSize: 25,
      pageSizeList: [25, 50, 100],
    },

    // Column auto-sizing max width possible 300px
    autoColumnSize: {
      syncLimit: 100,
      useHeaders: true,
    },

    rowHeights: 38,
    ...props.additionalTableSettings,
  };
});

async function loadHandsontableFiles() {
  try {
    isLoading.value = true;
    ['handsontable-js', 'handsontable-css'].forEach(removeLoadedFile);
    // Load Handsontable CSS
    await load_js_css_file(
      'https://cdn.jsdelivr.net/npm/handsontable@16.1.0/styles/handsontable.min.css',
      'bi-handsontable-css',
      'css',
    );

    // Load Handsontable theme CSS
    await load_js_css_file(
      'https://cdn.jsdelivr.net/npm/handsontable@16.1.0/styles/ht-theme-main.css',
      'bi-handsontable-theme-css',
      'css',
    );

    isLoading.value = false;
  }
  catch (error) {
    console.error('Error loading Handsontable files:', error);
    isLoading.value = false;
  }
}
function customRenderer(instance, td, row, col, prop, value, cellProperties) {
  // Apply skeleton loader if needed
  if (props.showSkeletonLoader) {
    td.innerHTML = '<div class="skeleton-loader"></div>';
    td.className = 'skeleton-cell';
    return td;
  }

  // Get row data
  const physical_row = instance.toPhysicalRow(row);
  const row_data = instance.getSourceDataAtRow?.(physical_row);
  const column_key = props.columns[col]?.data || props.columns[col]?.title || col;

  const div_content = document.createElement('div');
  div_content.style.display = 'flex';
  div_content.style.alignItems = 'center';
  div_content.style.justifyContent = 'justify-start';
  div_content.style.width = '100%';

  // Add custom chevron
  if (row_data.__is_group && row_data.__is_group_column === column_key) {
    const collapsingUI = instance.getPlugin('nestedRows').collapsingUI;

    const is_expanded = !collapsingUI.collapsedRows.includes(physical_row);

    // Inside the customRenderer function, update the chevron HTML:
    const chevron = document.createElement('span');
    chevron.innerHTML = `
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" class="bi-exportable-icon">
      <path d="M6 9L12 15L18 9" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>
    `;
    chevron.style.cssText = `
      cursor: pointer;
      transform: ${is_expanded ? 'rotate(0deg)' : 'rotate(-90deg)'};
      display: flex;
      align-items: center;
      margin-right: 4px;
      flex-shrink: 0;
    `;

    chevron.addEventListener('click', (e) => {
      e.stopPropagation(); // prevent triggering cell edit

      if (is_expanded) {
        collapsingUI.collapseChildren(physical_row);
      }
      else {
        collapsingUI.expandChildren(physical_row);
      }
      instance.render(); // refresh chevron icon
    });

    div_content.appendChild(chevron);
  }

  // Add content
  const column_type = props.columns[col]?.data_type;
  if (column_type === 'boolean') {
    // eslint-disable-next-line prefer-rest-params
    window.BiHandsontable.renderers.CheckboxRenderer.apply(this, arguments);
    td.classList.remove('htDimmed');
  }
  else {
    if (!row_data.__is_grand_total && ((props.columns[col].resource_type === 'members' && props.columns[col].type === 'text') || props.columns[col].data_type === 'array')) {
      const tag_type = props.columns[col].resource_type === 'members' ? 'member' : 'tag';
      let tags_content = document.createElement('div');
      tags_content = tagsRenderer(instance, tags_content, row, col, prop, value, { tag_type });
      div_content.appendChild(tags_content);
    }
    else if (props.columns[col]?.has_custom_content) {
      const content = props.getCustomCellContent(column_type, value);
      div_content.appendChild(content);
    }
    else {
      const content = document.createElement('span');
      content.textContent = value || '';
      div_content.appendChild(content);
    }

    td.textContent = ''; // Clear existing content
    td.appendChild(div_content);
  }

  // Add padding using inline styles (not CSS classes)
  td.style.fontSize = localStorage.getItem('bi-table-font-size') || '13px';

  // Apply column background color if configured
  const column_color = props.columnConfig[column_key]?.backgroundColor;

  if (column_color) {
    td.style.backgroundColor = column_color;
  }
  else {
    td.style.backgroundColor = '#FFFFFF';
  }

  const style = props.getCellFormatting({
    row_index: row,
    column_index: col,
    value,
    row_data,
    column: props.columns[col],
    instance,
  });

  if (style) {
    Object.assign(td.style, style);
  }
  else {
    td.style.color = '#475467';
  }

  // Make cells readonly
  cellProperties.readOnly = true;

  return td;
}
customRenderer.fastRenderer = true;

function createHeaderRenderer() {
  return function (col, TH) {
    // Check if this is a leaf level header (actual column header, not nested group header)
    const thead = TH.closest('thead');
    const isNestedHeader = thead?.querySelectorAll('tr').length > 1;
    const currentRow = TH.closest('tr');
    const headerLevel = Array.from(thead.querySelectorAll('tr')).indexOf(currentRow);
    const hasColspan = TH.getAttribute('colspan') && Number.parseInt(TH.getAttribute('colspan')) > 1;

    // Only apply custom rendering to leaf level headers (bottom row in nested structure or single row)
    const isLeafHeader = !hasColspan && (!isNestedHeader || headerLevel === thead.querySelectorAll('tr').length - 1);

    const div = TH.querySelector('div[role="presentation"]');
    div.style.padding = '0px';
    TH.style.textAlign = 'left';

    const span = div.querySelector('span');
    if (!isLeafHeader) {
      if (span.classList.contains('columnSorting')) {
        span.classList.remove('columnSorting');
      }
      TH.style.padding = '8px 18px';
      return;
    }
    TH.classList.add('group');
    TH.style.padding = '8px 0px';
    if (col === -1)
      return;

    // Remove context menu bind and replace with custom dropdown menu open
    if (!TH._dropdownBound) {
      TH._dropdownBound = true;

      TH.addEventListener(
        'contextmenu',
        (e) => {
          e.preventDefault(); // stop browser & HT context menu
          e.stopPropagation();

          const filter_btn = TH.querySelector('.changeType');
          if (!filter_btn)
            return;
          filter_btn.click();
        },
        true, // <— MUST use capture phase
      );
    }

    const columnData = props.columns[col];
    const columnKey = columnData?.data || columnData?.title || col;
    const columnColor = props.columnConfig[columnKey]?.backgroundColor;
    if (columnColor) {
      TH.style.backgroundColor = columnColor;
    }

    // Add padding and styling directly to the header
    div.style.display = 'flex';
    div.style.alignItems = 'center';
    div.style.justifyContent = 'space-between';

    if (!table_settings.value?.multiColumnSorting) {
      TH.style.padding = '8px 0px 8px 18px';
    }
    if (table_settings.value?.dropdownMenu) {
      div.style.paddingRight = '8px';
    }

    // Enable text wrapping for column headers
    const headerSpan = TH.querySelector('span.colHeader');
    if (headerSpan) {
      headerSpan.style.whiteSpace = 'normal';
      headerSpan.style.wordWrap = 'break-word';
      headerSpan.style.lineHeight = '1.4';
    }
  };
}

function afterColumnResize() {
  const column_widths_map = {};
  for (let i = 0; i < hotInstance.value.countCols(); i++) {
    const id = hotInstance.value.getColumnMeta(i).data;
    const width = hotInstance.value.getColWidth(i);
    column_widths_map[id] = width;
  }
  hotInstance.value.render();
  emit('columnResize', column_widths_map);
}

function afterColumnMove() {
  const totalCols = hotInstance.value.countCols();

  // Build new order of your columns based on visual positions
  const new_columns = [];

  for (let visualIndex = 0; visualIndex < totalCols; visualIndex++) {
    const physicalIndex = hotInstance.value.toPhysicalColumn(visualIndex);
    // physicalIndex is the original index in your data/settings
    const col = { ...props.columns[physicalIndex] };
    col.order_index = visualIndex;
    new_columns.push(col);
  }

  logger.log('Updated columns after move:', new_columns);
  emit('columnMove', new_columns);
}

function afterColumnSort(_, destinationSortConfigs) {
  const sorted_columns = destinationSortConfigs.map((config) => {
    const column_details = props.columns[config.column];
    return {
      column: column_details?.data || column_details?.title,
      sortOrder: config.sortOrder,
    };
  });
  emit('columnSort', sorted_columns);
}

function updateTableSettings(custom_settings = {}) {
  setTimeout(() => {
    hotInstance.value.updateSettings({
      ...table_settings.value,
      ...custom_settings,
    });
  }, 100);
}

function afterSelectionEnd() {
  const ranges = this.getSelectedRange();
  const total_rows = this.countRows();

  // Selection cleared (clicked outside)
  if (!ranges) {
    emit('selectionEnd', { selected_columns: [], selected_rows: [] });
    return;
  }

  const fully_selected_columns = new Set();
  const partially_selected_columns_set = new Set();
  const seen_rows = new Set();
  const selected_rows = [];

  // Determine which columns are fully selected
  ranges.forEach(({ from, to }) => {
    const col_start = Math.min(from.col, to.col);
    const col_end = Math.max(from.col, to.col);

    // Normalize header selections (-1 → 0)
    const row_start = Math.max(0, Math.min(from.row, to.row));
    const row_end = Math.max(from.row, to.row);

    const is_full_column = (row_start === 0) && (row_end === total_rows - 1);

    if (is_full_column) {
      for (let col = col_start; col <= col_end; col++) {
        fully_selected_columns.add(col);
      }
    }
    else {
      for (let col = col_start; col <= col_end; col++) {
        partially_selected_columns_set.add(col);
      }
    }

    for (let row = row_start; row <= row_end; row++) {
      const physical_row = this.toPhysicalRow(row);

      // Avoid duplicates from overlapping ranges
      if (!seen_rows.has(physical_row)) {
        seen_rows.add(physical_row);
        selected_rows.push(this.getSourceData()[physical_row]);
      }
    }
  });

  // Use column headers (safe for all configurations)
  const settings = this.getSettings();
  const columns = settings.columns;
  const selected_columns = [...fully_selected_columns].map(visual_col => columns[visual_col]);
  const partially_selected_columns = [...partially_selected_columns_set].map(visual_col => columns[visual_col]);

  emit('selectionEnd', { selected_columns, selected_rows, partially_selected_columns });
}
function afterDeselect() {
  emit('selectionEnd', { selected_columns: [], selected_rows: [], partially_selected_columns: [] });
}

async function initializeTable() {
  if (!window.BiHandsontable)
    return;

  if (props.canEdit) {
    registerMultiSelect();
    registerDateTime();
  }
  const defaultCellProps = { renderer: customRenderer };
  const headerRenderersCache = {};
  const settings = {
    autoColumnSize: false,
    height: props.height,
    width: props.width,
    fillHandle: false,
    loading: true,
    licenseKey: import.meta.env.VITE_APP_HOT_LICENSE_KEY,

    manualColumnResize: true,

    themeName: 'ht-theme-main',

    // Key setting to stretch columns
    stretchH: 'all',

    // Table styling for clean look
    className: 'htLeft htMiddle',

    // Disable borders
    customBorders: false,

    ...(props.canEdit
      ? {}
      : {
          // Custom cell renderer
          cells() {
            return defaultCellProps;
          },
        }),

    // Column header renderer
    afterGetColHeader(col, TH) {
      if (!headerRenderersCache[col]) {
        headerRenderersCache[col] = createHeaderRenderer(col);
      }
      headerRenderersCache[col](col, TH);
    },

    // Column resize callback
    afterColumnResize,

    // Column sort callback
    afterColumnSort,

    // Column move callback
    afterColumnMove,

    // Column selection callback
    afterSelectionEnd,
    afterDeselect,

    afterLoadData() {
      // eslint-disable-next-line ts/no-this-alias
      const hot = this;
      emit('afterLoadData', hot);
    },

    ...table_settings.value,
  };
  const container = document.getElementById(props.biTableId);
  if (container)
    container.innerHTML = '';

  hotInstance.value = new window.BiHandsontable(container, settings);
  // Register in global registry so PDF export can access the instance
  window.__biHotInstances.set(props.biTableId, hotInstance.value);
  emit('tableInstance', {
    hotInstance: hotInstance.value,
    updateTableSettings,
  });
  isLoading.value = false;
}

onMounted(async () => {
  isLoading.value = true;
  await loadHandsontableFiles();

  import('https://cdn.jsdelivr.net/npm/handsontable@16.1.0/+esm').then((module) => {
    if (!window.BiHandsontable) {
      window.BiHandsontable = module.default;
      initializeTable();
    }
    else {
      initializeTable();
    }
  });
});

onUnmounted(() => {
  window.__biHotInstances?.delete(props.biTableId);
  if (hotInstance.value)
    hotInstance.value.destroy();
  window.BiHandsontable = null;
});
</script>

<template>
  <HawkLoader v-if="isLoading" />
  <div class="bi-handsontable-wrapper">
    <div :id="biTableId" class="ht-theme-main" />
  </div>
</template>

<style lang="scss">
.ht-theme-main {
  --ht-accent-color: #1570EF !important;
  --ht-cell-editor-border-color: #1570EF !important;
  --ht-cell-editor-shadow-color: #1570EF !important;
  --ht-cell-selection-border-color: #1570EF !important;
  --ht-cell-autofill-background-color: #1570EF !important;
  --ht-cell-mobile-handle-border-color: #1570EF !important;
  --ht-move-indicator-color: #1570EF !important;
  --ht-header-active-background-color: #1570EF !important;
  --ht-header-row-active-background-color: #1570EF !important;
  --ht-checkbox-focus-ring-color: #1570EF !important;
  --ht-checkbox-checked-background-color: #1570EF !important;
  --ht-checkbox-checked-focus-background-color: #1570EF !important;
  --ht-checkbox-indeterminate-background-color: #1570EF !important;
  --ht-checkbox-indeterminate-focus-background-color: #1570EF !important;
  --ht-radio-focus-ring-color: #1570EF !important;
  --ht-radio-checked-border-color: #1570EF !important;
  --ht-radio-checked-icon-color: #1570EF !important;
  --ht-icon-button-active-border-color: #1570EF !important;
  --ht-icon-button-active-background-color: #1570EF !important;
  --ht-primary-button-background-color: #1570EF !important;
  --ht-primary-button-focus-background-color: #1570EF !important;
  --ht-comments-textarea-focus-border-color: #1570EF !important;
  --ht-comments-indicator-color: #1570EF !important;
  --ht-link-color: #1570EF !important;
  --ht-input-focus-border-color: #1570EF !important;

  --ht-header-active-border-color: #1570EF !important;
  --ht-checkbox-checked-border-color: #1570EF !important;
  --ht-checkbox-indeterminate-border-color: #1570EF !important;
  --ht-radio-checked-focus-border-color: #1570EF !important;
  --ht-radio-checked-focus-icon-color: #1570EF !important;
  --ht-icon-button-active-hover-border-color: #1570EF !important;
  --ht-icon-button-active-hover-background-color: #1570EF !important;
  --ht-cell-vertical-padding: 8px !important;
}
</style>

<style scoped lang="scss">
.bi-handsontable-wrapper {
  &:deep(.handsontable tr td:first-child) {
    border-left: 1px solid #e0e0e0 !important;
  }
  &:deep(.handsontable .changeType) {
    margin-right: 2px !important;
    margin-top: -2px !important;
  }

  // Simple row hover highlighting with CSS only
  // &:deep(.handsontable tbody tr:hover td) {
  //   background-color: #EEF4F9 !important;
  //   transition: background-color 0.15s ease;
  // }

  &:deep(.handsontable) {
    .htCore th, td {
      max-width: 300px !important;
    }
    th.htFiltersActive {
      background-color: var(--ht-header-filter-background-color) !important;
    }
    th.ht__active_highlight {
      background-color: #1570EF !important;
      color: #FFFFFF !important;
    }
    .htDimmed {
      background-color: #FFFFFF !important;
      color: #475467 !important;
    }
    .htCheckboxRendererInput {
      border: 1px solid #cbd5e1;
      border-radius: 4px;
    }
    td {
      padding: 8px 8px 8px 16px !important;
      text-align: left !important;
      vertical-align: middle !important;
    }
    .ht_master {
      box-sizing: border-box;
    }
    .htCore {
      font-family: "Inter var" !important;
    }

    span.colHeader {
      font-size: 13px !important;
      white-space: normal !important;
      word-wrap: break-word !important;
      line-height: 1.4 !important;
      display: inline-block !important;
    }

    // Enable text wrapping in column headers
    thead th {
      white-space: normal !important;
      vertical-align: middle !important;
      height: auto !important;
    }

    // Ensure header rows adjust height automatically
    thead tr {
      height: auto !important;
    }

    // &.ht_clone_top, &.ht_clone_top_left_corner, &.ht_clone_left, &.ht_clone_inline_start {
    //   z-index: 1;
    // }
    .ht_clone_left .htCore th {
      white-space: normal !important;
    }

    // Vertically center row header numbers
    tbody th > div {
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      height: 100% !important;
    }
  }
}

// Skeleton loader styles
:deep(.skeleton-loader) {
  width: 100%;
  height: 16px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: skeleton-loading 1.5s infinite;
  border-radius: 4px;
}

:deep(.skeleton-cell) {
  padding: 8px !important;
}

@keyframes skeleton-loading {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}
</style>
